import React, { useState, useRef, useEffect } from "react";
import Pagination from "../../components/Pagination";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../../utils/axios";
import SearchableSelect from "../SearchableSelect";

export default function JobFileTable({
  jobs = [],
  onView,
  onEdit,
  onDelete,
  fullHeight = false,
  search: searchProp,
  onSearchChange,
  currentPage: currentPageProp,
  itemsPerPage: itemsPerPageProp,
  onPageChange,
  onItemsPerPageChange,
  focusedJobId = null,
  onFocusJob,
  getJobFileBackLink,
  branches = [],
  branchFilter = "all",
  onBranchFilterChange,
}) {
  const [currentPageLocal, setCurrentPageLocal] = useState(1);
  const [itemsPerPageLocal, setItemsPerPageLocal] = useState(100);
  const [searchLocal, setSearchLocal] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const location = useLocation();
  const navigate = useNavigate();

  const actionRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [rightOffset, setRightOffset] = useState(0);
  const isControlledPagination =
    typeof currentPageProp === "number" &&
    typeof itemsPerPageProp === "number" &&
    typeof onPageChange === "function" &&
    typeof onItemsPerPageChange === "function";
  const isControlledSearch = typeof onSearchChange === "function";
  const currentPage = isControlledPagination ? currentPageProp : currentPageLocal;
  const itemsPerPage = isControlledPagination ? itemsPerPageProp : itemsPerPageLocal;
  const search = isControlledSearch ? searchProp ?? "" : searchLocal;

  useEffect(() => {
    if (isControlledSearch || isControlledPagination) return;
    const params = new URLSearchParams(location.search);
    const s = params.get("search");
    if (s) {
      setSearchLocal(s);
      setCurrentPageLocal(1);
    }
  }, [location.search, isControlledSearch, isControlledPagination]);

  const filtered = jobs.filter((x) => {
    const key = search.toLowerCase();
    return (
      (x.CustomerName || "").toLowerCase().includes(key) ||
      (x.ContractCustomerName || "").toLowerCase().includes(key) ||
      (x.Contract_No || "").toLowerCase().includes(key) ||
      (x.Quote_No || "").toLowerCase().includes(key) ||
      (x.Tel || "").toLowerCase().includes(key) ||
      (x.ServiceName || "").toLowerCase().includes(key)
    );
  });

  // Sort function
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    if (isControlledPagination) onPageChange(1);
    else setCurrentPageLocal(1);
  };

  // Apply sorting
  const sorted = [...filtered].sort((a, b) => {
    if (!sortColumn) return 0;

    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    // Handle different data types
    if (sortColumn === "LastServiceDate" || sortColumn === "LastPaymentDate") {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    } else if (sortColumn === "LastServiceRound") {
      aVal = aVal === "-" || !aVal ? 0 : parseInt(aVal) || 0;
      bVal = bVal === "-" || !bVal ? 0 : parseInt(bVal) || 0;
    } else {
      aVal = (aVal || "").toString().toLowerCase();
      bVal = (bVal || "").toString().toLowerCase();
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const totalItems = sorted.length;
  const from = (currentPage - 1) * itemsPerPage;
  const paginated = sorted.slice(from, from + itemsPerPage);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("th-TH", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-";

  useEffect(() => {
    if (actionRef.current) {
      setRightOffset(actionRef.current.getBoundingClientRect().width);
    }
  }, []);

  useEffect(() => {
    if (focusedJobId == null) return;
    const wrapper = tableScrollRef.current;
    if (!wrapper) return;
    const activeRow = wrapper.querySelector("tr.row-focused");
    if (!activeRow) return;
    activeRow.scrollIntoView({ block: "center", behavior: "auto" });
  }, [focusedJobId, currentPage, itemsPerPage, paginated.length]);

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm"
      style={{
        display: "flex",
        flexDirection: "column",
        height: fullHeight ? "100%" : undefined,
        maxHeight: fullHeight ? "none" : "calc(91vh - 320px)",
        overflow: "hidden",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }

        .table-wrapper-scroll { padding: 0 0 0 20px !important; }

        table.table {
            border-collapse: separate !important;
            border-spacing: 1px !important;
            width: 100%;
            min-width: 1500px;
        }

        th, td {
            padding: 8px 10px !important;
            background: white !important;
            text-align: center;
            outline: 1px solid #d0d0d0;
            outline-offset: -1px;
            white-space: nowrap;
        }

        /* --- HEADER STYLE --- */
        thead th {
            background-color: #E13F57 !important;
            color: white !important;
            font-weight: 600;
            text-align: center;
            position: sticky !important;
            top: 0 !important;
            z-index: 1100 !important;
            cursor: default;
        }

        thead th.sortable {
            cursor: pointer;
            user-select: none;
            position: relative;
            padding-right: 25px !important;
        }

        thead th.sortable:hover {
            background-color: #c9354d !important;
        }

        thead th .sort-icon {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 16px;
            opacity: 0.7;
        }

        thead th.sortable.active .sort-icon {
            opacity: 1;
        }

        /* ให้ sticky ก็ยังเป็นสีเดียวกัน */
        .sticky-action {
            position: sticky;
            right: 0;
            background: white !important;
            z-index: 20;
            isolation: isolate;
            color: white !important;
        }

        thead th.sticky-action {
            background-color: #E13F57 !important;
            top: 0 !important;
            z-index: 1101 !important;
        }

        thead th.sticky-action.sortable:hover {
            background-color: #c9354d !important;
        }

        /* Customer name link style */
        td .customer-name-link {
            color: #2563eb !important;
            text-decoration: underline !important;
            text-decoration-thickness: 1px !important;
            text-underline-offset: 3px !important;
            font-weight: bold !important;
            cursor: pointer !important;
            transition: color 0.2s;
            display: inline-block;
            position: relative;
            z-index: 1;
            pointer-events: auto !important;
        }

        td .customer-name-link:hover {
            color: #1d4ed8 !important;
            text-decoration: underline !important;
            text-decoration-thickness: 1px !important;
            font-weight: bold !important;
        }

        /* Ensure td with link is clickable */
        td.text-start {
            position: relative;
            z-index: 2;
        }

        /* Make sure link is above everything except sticky action */
        tbody td:not(.sticky-action) {
            position: relative;
            z-index: 1;
        }

        tbody td .customer-name-link {
            z-index: 10 !important;
        }

        /* Make quote and contract links clickable */
        tbody td a,
        tbody td span.cursor-pointer {
            position: relative !important;
            z-index: 1000 !important;
            pointer-events: auto !important;
            display: inline-block !important;
            cursor: pointer !important;
        }

        /* Ensure td containing links is clickable */
        tbody td:has(a),
        tbody td:has(span.cursor-pointer) {
            pointer-events: auto !important;
            position: relative !important;
        }

        /* Prevent any overlay from blocking links */
        tbody td a:hover,
        tbody td span.cursor-pointer:hover {
            color: #1e40af !important;
        }

        /* Make sure links are above everything */
        tbody td a,
        tbody td span.cursor-pointer {
            z-index: 1000 !important;
        }

        tbody tr.row-focused td {
            background: #fdf2f8 !important;
        }

        tbody tr.row-focused:hover td {
            background: #fdf2f8 !important;
        }
        `}</style>

      <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <h5 className="m-0 fw-bold">รายการแฟ้มงาน</h5>

          <div className="relative">
            <input
              type="text"
              placeholder="ค้นหา..."
              value={search}
              onChange={(e) => {
                if (isControlledSearch) onSearchChange(e.target.value);
                else setSearchLocal(e.target.value);
                if (isControlledPagination) onPageChange(1);
                else setCurrentPageLocal(1);
              }}
              className="h-[36px] w-[250px] border border-gray-300 rounded-md px-3 pr-8 py-1 text-sm"
            />

            {search !== "" && (
              <button
                onClick={() => {
                  if (isControlledSearch) onSearchChange("");
                  else setSearchLocal("");
                  if (isControlledPagination) onPageChange(1);
                  else setCurrentPageLocal(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon="mdi:close" className="w-4 h-4" />
              </button>
            )}
          </div>

          <div style={{ minWidth: "220px" }}>
            <SearchableSelect
              value={branchFilter}
              onChange={(value) => onBranchFilterChange?.(String(value))}
              options={[
                { value: "all", label: "สาขา: ทั้งหมด" },
                ...branches.map((b) => ({
                  value: String(b.Branch_Id),
                  label: b.Branch_Name,
                })),
              ]}
              placeholder="สาขา: ทั้งหมด"
              searchPlaceholder="ค้นหาชื่อสาขา..."
              className="h-[36px]"
              triggerClassName="text-sm"
              triggerStyle={{ height: "36px", paddingTop: "4px", paddingBottom: "4px" }}
              renderValue={(selected) => selected?.label || "สาขา: ทั้งหมด"}
            />
          </div>
        </div>
      </div>

      <div
        ref={tableScrollRef}
        className="table-wrapper-scroll flex-1"
        style={{ overflowY: "auto", overflowX: "auto", flexGrow: 1 }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th>ลำดับ</th>
              <th 
                className={`sortable ${sortColumn === "CustomerName" ? "active" : ""}`}
                onClick={() => handleSort("CustomerName")}
              >
                ชื่อลูกค้า
                {sortColumn === "CustomerName" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "CustomerName" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "ContractCustomerName" ? "active" : ""}`}
                onClick={() => handleSort("ContractCustomerName")}
              >
                ชื่อที่ใช้ในสัญญา
                {sortColumn === "ContractCustomerName" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "ContractCustomerName" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "Contract_No" ? "active" : ""}`}
                onClick={() => handleSort("Contract_No")}
              >
                เลขที่สัญญา
                {sortColumn === "Contract_No" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "Contract_No" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "Quote_No" ? "active" : ""}`}
                onClick={() => handleSort("Quote_No")}
              >
                ใบเสนอราคา
                {sortColumn === "Quote_No" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "Quote_No" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "Tel" ? "active" : ""}`}
                onClick={() => handleSort("Tel")}
              >
                โทร
                {sortColumn === "Tel" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "Tel" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "LineId" ? "active" : ""}`}
                onClick={() => handleSort("LineId")}
              >
                Line ID
                {sortColumn === "LineId" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "LineId" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "ServiceName" ? "active" : ""}`}
                onClick={() => handleSort("ServiceName")}
              >
                บริการ
                {sortColumn === "ServiceName" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "ServiceName" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "Address" ? "active" : ""}`}
                onClick={() => handleSort("Address")}
              >
                สถานที่บริการ
                {sortColumn === "Address" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "Address" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "LastServiceRound" ? "active" : ""}`}
                onClick={() => handleSort("LastServiceRound")}
              >
                ครั้งที่บริการล่าสุด
                {sortColumn === "LastServiceRound" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "LastServiceRound" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "LastServiceDate" ? "active" : ""}`}
                onClick={() => handleSort("LastServiceDate")}
              >
                วันที่บริการล่าสุด
                {sortColumn === "LastServiceDate" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "LastServiceDate" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th 
                className={`sortable ${sortColumn === "LastPaymentDate" ? "active" : ""}`}
                onClick={() => handleSort("LastPaymentDate")}
              >
                งวดชำระล่าสุด
                {sortColumn === "LastPaymentDate" && (
                  <Icon 
                    icon={sortDirection === "asc" ? "mdi:arrow-up" : "mdi:arrow-down"} 
                    className="sort-icon"
                  />
                )}
                {sortColumn !== "LastPaymentDate" && (
                  <Icon icon="mdi:unfold-more-horizontal" className="sort-icon" />
                )}
              </th>
              <th ref={actionRef} className="sticky-action">
                การจัดการ
              </th>
            </tr>
          </thead>

          <tbody>
            {paginated.length ? (
              paginated.map((row, i) => (
                <tr
                  key={row.JobId}
                  onClick={() => onFocusJob?.(row)}
                  className={
                    focusedJobId != null &&
                    String(row.JobId) === String(focusedJobId)
                      ? "row-focused"
                      : ""
                  }
                >
                  <td>{from + i + 1}</td>
                  <td className="text-start">
                    {onView && row.CustomerName ? (
                      <span
                        className="customer-name-link"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (onView) {
                            onView(row);
                          }
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onView) {
                              onView(row);
                            }
                          }
                        }}
                      >
                        {row.CustomerName}
                      </span>
                    ) : (
                      row.CustomerName || '-'
                    )}
                  </td>
                  <td className="text-start">{row.ContractCustomerName || '-'}</td>
                  <td style={{ position: 'relative', zIndex: 10 }}>
                    {row.JobId ? (
                      <a
                        href={`/contract/edit/${row.JobId}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const backLink = getJobFileBackLink?.(row) || "/jobfile";
                          navigate(`/contract/edit/${row.JobId}`, {
                            state: {
                              fromJobFile: true,
                              jobFileBackLink: backLink,
                              jobFileBackLabel: "แฟ้มงาน",
                            },
                          });
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        style={{ cursor: 'pointer', pointerEvents: 'auto', position: 'relative', zIndex: 11, textDecoration: 'underline' }}
                      >
                        {row.Contract_No}
                      </a>
                    ) : (
                      row.Contract_No
                    )}
                  </td>
                  <td style={{ position: 'relative', zIndex: 10, pointerEvents: 'auto' }}>
                    {row.Quote_No && row.Quote_No !== '-' && row.Quote_No !== '' ? (
                      <span
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Quote clicked:', { Quote_Id: row.Quote_Id, Quote_No: row.Quote_No, row });
                          
                          let quoteId = row.Quote_Id;
                          
                          // ถ้าไม่มี Quote_Id แต่มี Quote_No ให้ค้นหาจาก Quote_No
                          if (!quoteId || quoteId === null || quoteId === undefined || quoteId === '' || quoteId === 0) {
                            try {
                              const compId = localStorage.getItem("company");
                              const response = await api.post('/get-by-doc-no', {
                                table: 'QT_H',
                                doc_no: row.Quote_No,
                                comp_id: compId || 1
                              });
                              
                              if (response.data?.recordset?.length > 0) {
                                quoteId = response.data.recordset[0].Doc_Id;
                              }
                            } catch (error) {
                              console.error('Error fetching quote by Doc_No:', error);
                            }
                          }
                          
                          if (quoteId && quoteId !== null && quoteId !== undefined && quoteId !== '' && quoteId !== 0) {
                            const backLink = getJobFileBackLink?.(row) || "/jobfile";
                            navigate(`/quote/edit/${quoteId}`, {
                              state: {
                                fromJobFile: true,
                                jobFileBackLink: backLink,
                                jobFileBackLabel: "แฟ้มงาน",
                              },
                            });
                          } else {
                            Swal.fire({
                              icon: 'info',
                              title: 'ไม่พบข้อมูลใบเสนอราคา',
                              text: `ไม่สามารถเปิดใบเสนอราคา ${row.Quote_No} ได้`,
                            });
                          }
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                        style={{ 
                          cursor: 'pointer', 
                          pointerEvents: 'auto', 
                          position: 'relative', 
                          zIndex: 1000, 
                          textDecoration: 'none',
                          display: 'inline-block'
                        }}
                      >
                        {row.Quote_No}
                      </span>
                    ) : (
                      <span>{row.Quote_No || '-'}</span>
                    )}
                  </td>
                  <td>{row.Tel}</td>
                  <td>{row.LineId}</td>
                  <td className="text-start">{row.ServiceName}</td>
                  <td className="text-start">{row.Address}</td>
                  <td>{row.LastServiceRound || '-'}</td>
                  <td>{formatDate(row.LastServiceDate)}</td>
                  <td>{row.LastPaymentDate === '-' ? '-' : (row.LastPaymentDate ? formatDate(row.LastPaymentDate) : '-')}</td>

                  <td className="sticky-action">
                    <div className="d-flex gap-2 justify-center">
                      <button
                        className="btn btn-sm btn-warning text-white px-2 py-1"
                        style={{ 
                          backgroundColor: '#f59e0b',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          minWidth: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={() => onEdit?.(row)}
                        title="แก้ไข"
                      >
                        <Icon icon="mdi:pencil" width={18} />
                      </button>

                      <button
                        className="btn btn-sm text-white px-2 py-1"
                        style={{ 
                          backgroundColor: '#ef4444',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          minWidth: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={() => onDelete?.(row)}
                        title="ลบ"
                      >
                        <Icon icon="mdi:delete" width={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={13} className="text-center py-4 text-gray-500">
                  ไม่มีข้อมูลแฟ้มงาน
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ flexShrink: 0 }}>
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onItemsPerPageChange={(val) => {
            if (isControlledPagination) {
              onItemsPerPageChange(val);
              onPageChange(1);
            } else {
              setItemsPerPageLocal(val);
              setCurrentPageLocal(1);
            }
          }}
          onPageChange={(page) => {
            if (isControlledPagination) onPageChange(page);
            else setCurrentPageLocal(page);
          }}
        />
      </div>
    </div>
  );
}
